import React, { useState, useEffect } from "react";
import { ErrorMessage, Field, useField } from "formik";
import Select from "react-select";
import Typesense from "typesense";

export function TextField({name, label, placeholder = '', classes= []}) {
  classes.push('my-2');
  return (
    <div className={classes.join(' ')}>
      <label className='form-label' htmlFor={name}>{label}</label>
      <Field className='form-control form-control-sm' name={name} placeholder={placeholder} type='text'/>
      <ErrorMessage name={name} component='p' className='field-error mt-2 mb-0'/>
    </div>
  );
}

export function ColorField({name, label, placeholder = '', classes= []}) {
  classes.push('my-2');
  return (
    <div className={classes.join(' ')}>
      <label className='form-label' htmlFor={name}>{label}</label>
      <Field className='form-control form-control-sm' name={name} placeholder={placeholder} type='color'/>
      <ErrorMessage name={name} component='p' className='field-error mt-2 mb-0'/>
    </div>
  );
}

export function TextAreaField({name, label}) {
  return (
    <div className='my-2'>
      <label className='form-label' htmlFor={name}>{label}</label>
      <Field className='form-control form-control-sm' name={name} placeholder='' as='textarea' rows={6}/>
      <ErrorMessage name={name} component='p' className='field-error mt-2 mb-0'/>
    </div>
  );
}

export function Checkbox({name, label}) {
  return (
    <div className='my-2'>
      <label className='form-label' htmlFor={name}>{label}</label>
      <div className='form-control form-control-sm'>
        <Field className='' name={name} type='checkbox'/>
      </div>
      <ErrorMessage name={name} component='p' className='field-error mt-2 mb-0'/>
    </div>
  );
}

export function DropdownField({name, label, options}) {
  return (
    <div className='my-2 w-100'>
      {label?.trim().length > 0 && <label className='form-label' htmlFor={name}>{label}</label>}
      <Field as='select' name={name} className='form-control form-control-sm'>
        {options.map(opt => <option key={opt[1]} value={opt[0]}>{opt[1]}</option>)}
      </Field>
      <ErrorMessage name={name} component='p' className='field-error mt-2 mb-0'/>
    </div>
  );
}

async function fetchTypesenseFacetValues({attribute, defaultIndex, filterBy}) {
  const tsHostElement = document.querySelector('meta[name="typesense-host"]');
  const tsKeyElement = document.querySelector('meta[name="typesense-key"]');

  if (!tsHostElement || !tsKeyElement) {
    console.error('Typesense configuration not found');
    return [];
  }

  const client = new Typesense.Client({
    nodes: [
      {host: tsHostElement.content, port: 443, protocol: 'https'}
    ],
    apiKey: tsKeyElement.content,
    connectionTimeoutSeconds: 2,
  });

  const searchParameters = {
    q: "*",
    query_by: "*",
    facet_by: attribute,
    max_facet_values: 100,
    per_page: 0,
  };

  if (filterBy) {
    searchParameters.filter_by = filterBy;
  }

  const result = await client
    .collections(defaultIndex)
    .documents()
    .search(searchParameters);

  const facet = result?.facet_counts?.find(f => f.field_name === attribute);
  return facet?.counts?.map(c => c.value) || [];
}

async function fetchOpenSearchFacetValues({attribute, defaultIndex, filterBy}) {
  let filterClauses = [];
  if (filterBy) {
    try {
      const parsed = JSON.parse(filterBy);
      if (Array.isArray(parsed)) {
        filterClauses = parsed.map(f => ({terms: {[f.field]: f.values}}));
      }
    } catch {
      // ignore malformed filterBy — behave like no filter
    }
  }

  const body = {
    size: 0,
    aggs: {
      [attribute]: {terms: {field: attribute, size: 1000}},
    },
  };

  if (filterClauses.length > 0) {
    body.query = {bool: {filter: filterClauses}};
  }

  const headers = {
    'Content-Type': 'application/json',
    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
  };
  const previewToken = document.getElementById('library-preview-token')?.dataset?.value;
  if (previewToken) headers['x-preview-token'] = previewToken;

  const host = `${location.protocol}//${location.host}`;
  const response = await fetch(`${host}/${defaultIndex}/_search`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) throw new Error(`OpenSearch ${response.status} for ${defaultIndex}`);

  const data = await response.json();
  return (data.aggregations?.[attribute]?.buckets || []).map(b => String(b.key));
}

export function MultiSelectField({name, label, attribute, defaultIndex, filterBy, provider}) {
  const [field, , helpers] = useField(name);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!attribute || !defaultIndex) {
      setOptions([]);
      return;
    }

    async function fetchFacetValues() {
      setLoading(true);
      try {
        const values = provider === 'opensearch'
          ? await fetchOpenSearchFacetValues({attribute, defaultIndex, filterBy})
          : await fetchTypesenseFacetValues({attribute, defaultIndex, filterBy});

        setOptions(values.map(v => ({value: v, label: v})));
      } catch (error) {
        console.error('Error fetching facet values:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }

    fetchFacetValues();
  }, [attribute, defaultIndex, filterBy, provider]);

  // Convert field value (array of strings) to Select format
  const selectedValues = Array.isArray(field.value)
    ? field.value.map(v => ({value: v, label: v}))
    : [];

  const handleChange = (selected) => {
    const values = selected ? selected.map(opt => opt.value) : [];
    helpers.setValue(values);
  };

  // Custom styles for better legibility
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? '#fff' : '#212529',
      backgroundColor: state.isSelected
        ? '#0d6efd'
        : state.isFocused
          ? '#e9ecef'
          : '#fff',
      fontWeight: state.isSelected ? '500' : '400',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#0d6efd',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#fff',
      fontWeight: '500',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#fff',
      ':hover': {
        backgroundColor: '#0b5ed7',
        color: '#fff',
      },
    }),
    control: (provided) => ({
      ...provided,
      borderColor: '#dee2e6',
      minHeight: '38px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
    }),
  };

  return (
    <div className='my-2'>
      <label className='form-label' htmlFor={name}>{label}</label>
      <Select
        isMulti
        name={name}
        options={options}
        value={selectedValues}
        onChange={handleChange}
        isLoading={loading}
        placeholder={loading ? "Loading facet values..." : "Select default values..."}
        classNamePrefix="react-select"
        isClearable
        styles={customStyles}
      />
      <ErrorMessage name={name} component='p' className='field-error mt-2 mb-0'/>
    </div>
  );
}
